import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, ClockCircleOutlined, EyeOutlined, CalendarOutlined, TagsOutlined, FileTextOutlined } from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import "./Blog.css";

const BlogDetail = () => {
  const { slug }   = useParams();
  const navigate   = useNavigate();

  const [blog, setBlog]         = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sidebarSearch, setSidebarSearch] = useState("");

  const fetchBlog = async () => {
    setLoading(true);
    try {
      // Fetch current blog + full post list in parallel
      const [res, listRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/Blog/${slug}`),
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/Blog?pageSize=50`),
      ]);

      const data = res.data?.data ?? res.data;
      setBlog(data);

      const raw  = listRes.data?.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setAllPosts(list);

      // Related = same category, exclude current
      if (data?.category) {
        setRelated(
          list.filter((b) => b.category === data.category && b.slug !== slug).slice(0, 3)
        );
      }
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { if (slug) fetchBlog(); }, [slug]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";

  const formatDateShort = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  const stripHtml = (html) =>
    html ? html.replace(/<[^>]*>/g, "").slice(0, 120) + "…" : "";

  // Posts shown in sidebar nav (exclude current, apply search filter)
  const sidebarPosts = allPosts
    .filter((b) => b.slug !== slug)
    .filter((b) =>
      !sidebarSearch ||
      b.title?.toLowerCase().includes(sidebarSearch.toLowerCase())
    );

  if (loading) return (
    <>
      <HeaderLayout />
      <div className="bd-loading"><Spin size="large" /></div>
      <FooterLayout />
    </>
  );

  if (!blog) return (
    <>
      <HeaderLayout />
      <div className="bd-not-found">
        <div className="bd-nf-icon">🔍</div>
        <h2>Article not found</h2>
        <p>This post may have been moved or deleted.</p>
        <button className="bd-back-btn" onClick={() => navigate("/blogs")}>
          <ArrowLeftOutlined /> Back to Blog
        </button>
      </div>
      <FooterLayout />
    </>
  );

  return (
    <>
      <HeaderLayout />

      <div className="bd-root">

        {/* Breadcrumb */}
        <div className="bd-breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="bd-bc-sep">›</span>
          <span onClick={() => navigate("/blogs")}>Blog</span>
          <span className="bd-bc-sep">›</span>
          <span className="bd-bc-current">{blog.title}</span>
        </div>

        <div className="bd-layout">

          {/* ── Main article ── */}
          <article className="bd-article">

            <div className="bd-article-top">
              <button className="bd-back-inline" onClick={() => navigate("/blogs")}>
                <ArrowLeftOutlined /> All Articles
              </button>
              {blog.category && (
                <span className="bd-article-cat">{blog.category}</span>
              )}
            </div>

            <h1 className="bd-title">{blog.title}</h1>

            <div className="bd-meta-row">
              <span className="bd-meta-item">
                <CalendarOutlined /> {formatDate(blog.publishedAt || blog.createdAt)}
              </span>
              {blog.readingTimeMinutes > 0 && (
                <span className="bd-meta-item">
                  <ClockCircleOutlined /> {blog.readingTimeMinutes} min read
                </span>
              )}
              {blog.viewCount > 0 && (
                <span className="bd-meta-item">
                  <EyeOutlined /> {blog.viewCount.toLocaleString()} views
                </span>
              )}
            </div>

            {blog.coverImageUrl && (
              <div className="bd-cover-wrap">
                <img src={blog.coverImageUrl} alt={blog.title} className="bd-cover" />
              </div>
            )}

            {blog.excerpt && (
              <blockquote className="bd-excerpt">{blog.excerpt}</blockquote>
            )}

            <div
              className="bd-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {blog.tags?.length > 0 && (
              <div className="bd-tags">
                <TagsOutlined className="bd-tags-icon" />
                {blog.tags.map((t) => (
                  <span key={t} className="bd-tag">{t}</span>
                ))}
              </div>
            )}

            <div className="bd-back-cta">
              <button className="bd-back-btn" onClick={() => navigate("/blogs")}>
                <ArrowLeftOutlined /> Back to all articles
              </button>
            </div>
          </article>

          {/* ── Sidebar ── */}
          <aside className="bd-sidebar">

            {/* Article info card */}
            <div className="bd-sidebar-card">
              <div className="bd-sidebar-card-title">Article Info</div>
              <div className="bd-info-row">
                <span className="bd-info-label">Published</span>
                <span className="bd-info-val">{formatDateShort(blog.publishedAt || blog.createdAt)}</span>
              </div>
              {blog.readingTimeMinutes > 0 && (
                <div className="bd-info-row">
                  <span className="bd-info-label">Read time</span>
                  <span className="bd-info-val">{blog.readingTimeMinutes} min</span>
                </div>
              )}
              {blog.category && (
                <div className="bd-info-row">
                  <span className="bd-info-label">Category</span>
                  <span className="bd-info-val bd-info-cat">{blog.category}</span>
                </div>
              )}
              {blog.viewCount > 0 && (
                <div className="bd-info-row">
                  <span className="bd-info-label">Views</span>
                  <span className="bd-info-val">{blog.viewCount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* All posts navigation */}
            {allPosts.length > 1 && (
              <div className="bd-sidebar-card bd-nav-card">
                <div className="bd-sidebar-card-title">
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  More Articles
                </div>

                {/* Mini search */}
                <div className="bd-nav-search">
                  <input
                    className="bd-nav-search-input"
                    placeholder="Search posts..."
                    value={sidebarSearch}
                    onChange={(e) => setSidebarSearch(e.target.value)}
                  />
                </div>

                <div className="bd-nav-list">
                  {sidebarPosts.length === 0 ? (
                    <div className="bd-nav-empty">No posts found</div>
                  ) : (
                    sidebarPosts.map((b) => (
                      <div
                        key={b.blogId ?? b.slug}
                        className="bd-nav-item"
                        onClick={() => {
                          navigate(`/blog/${b.slug}`);
                          setSidebarSearch("");
                        }}
                      >
                        {b.coverImageUrl ? (
                          <img src={b.coverImageUrl} alt="" className="bd-nav-thumb" />
                        ) : (
                          <div className="bd-nav-thumb-placeholder" />
                        )}
                        <div className="bd-nav-item-body">
                          <div className="bd-nav-item-title">{b.title}</div>
                          <div className="bd-nav-item-meta">
                            {b.readingTimeMinutes > 0 && (
                              <span><ClockCircleOutlined /> {b.readingTimeMinutes} min</span>
                            )}
                            <span>{formatDateShort(b.publishedAt || b.createdAt)}</span>
                          </div>
                        </div>
                        <ArrowRightOutlined className="bd-nav-item-arrow" />
                      </div>
                    ))
                  )}
                </div>

                <button
                  className="bd-sidebar-btn"
                  onClick={() => navigate("/blogs")}
                  style={{ marginTop: 12 }}
                >
                  View all articles
                </button>
              </div>
            )}

            {/* Tags */}
            {blog.tags?.length > 0 && (
              <div className="bd-sidebar-card">
                <div className="bd-sidebar-card-title">Tags</div>
                <div className="bd-sidebar-tags">
                  {blog.tags.map((t) => (
                    <span key={t} className="bd-tag">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Related articles */}
        {related.length > 0 && (
          <div className="bd-related">
            <div className="bd-related-title">Related Articles</div>
            <div className="bd-related-grid">
              {related.map((b) => (
                <div
                  key={b.blogId ?? b.slug}
                  className="bd-related-card"
                  onClick={() => navigate(`/blog/${b.slug}`)}
                >
                  {b.coverImageUrl && (
                    <img src={b.coverImageUrl} alt={b.title} className="bd-related-img" />
                  )}
                  <div className="bd-related-body">
                    {b.category && <span className="bd-related-cat">{b.category}</span>}
                    <div className="bd-related-card-title">{b.title}</div>
                    <div className="bd-related-excerpt">
                      {b.excerpt || stripHtml(b.content)}
                    </div>
                    <span className="bd-related-read">Read <ArrowRightOutlined /></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      <FooterLayout />
    </>
  );
};

export default BlogDetail;
